import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Sparkles, X, Send, MessageCircle, Mail, CalendarClock, ArrowRight } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { API, whatsappLink, MAILTO, newToken } from "@/lib/api";

const START = "Hallo! Ich helfe Ihnen bei Fragen zu Webhaus, unserer kostenlosen Demo und Ihrem neuen Webauftritt.";
const SUGGEST = ["Wie funktioniert die kostenlose Demo?", "Ich habe noch keine Website.", "Ich möchte meine Website modernisieren.", "Ich möchte einen Termin."];
const FALLBACK = "Der Assistent ist gerade nicht erreichbar. Unser Team hilft Ihnen gerne persönlich weiter – per WhatsApp oder E-Mail.";

function parse(text) {
  const idx = text.search(/\n?\s*AKTIONEN\s*:/i);
  if (idx === -1) return { body: text.replace(/A?K?T?I?O?N?E?N?$/, ""), actions: [] };
  const raw = text.slice(idx).split(":")[1] || "";
  const actions = raw.toLowerCase().split(/[,\s]+/).filter((a) => ["demo", "termin", "whatsapp", "email"].includes(a));
  return { body: text.slice(0, idx).trim(), actions };
}

function Actions({ list, onAction }) {
  if (!list?.length) return null;
  const map = { demo: [ArrowRight, "Demo-Formular öffnen"], termin: [CalendarClock, "Terminplaner öffnen"], whatsapp: [MessageCircle, "WhatsApp öffnen"], email: [Mail, "E-Mail schreiben"] };
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {list.map((a) => {
        const [Icon, label] = map[a];
        const cls = "inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-2 text-[13px] font-semibold hover:border-ink";
        if (a === "whatsapp") return <a key={a} href={whatsappLink()} target="_blank" rel="noopener noreferrer" className={cls} data-testid="chat-action-whatsapp"><Icon className="h-3.5 w-3.5" />{label}</a>;
        if (a === "email") return <a key={a} href={MAILTO} className={cls} data-testid="chat-action-email"><Icon className="h-3.5 w-3.5" />{label}</a>;
        return <button key={a} type="button" onClick={() => onAction(a)} className={cls} data-testid={`chat-action-${a}`}><Icon className="h-3.5 w-3.5" />{label}</button>;
      })}
    </div>
  );
}

export default function ChatWidget({ open, setOpen }) {
  const { openDemo, setBookingOpen } = useUI();
  const [msgs, setMsgs] = useState([{ role: "assistant", text: START }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const session = useRef(sessionStorage.getItem("wh_chat") || newToken());
  const listRef = useRef(null);

  useEffect(() => { sessionStorage.setItem("wh_chat", session.current); }, []);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [msgs]);

  const onAction = (a) => {
    setOpen(false);
    setTimeout(() => (a === "demo" ? openDemo() : setBookingOpen(true)), 150);
  };

  const send = async (text) => {
    const q = text.trim().slice(0, 500);
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "", streaming: true }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: session.current, message: q }) });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        patch(() => ({ role: "assistant", text: typeof d?.detail === "string" ? d.detail : FALLBACK, actions: ["whatsapp", "email"], error: true }));
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "", full = "", failed = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const ev = JSON.parse(p.slice(5));
          if (ev.error) failed = true;
          if (ev.t) { full += ev.t; const { body } = parse(full); patch((x) => ({ ...x, text: body })); }
        }
      }
      if (failed || !full.trim()) patch(() => ({ role: "assistant", text: FALLBACK, actions: ["whatsapp", "email"], error: true }));
      else { const { body, actions } = parse(full); patch(() => ({ role: "assistant", text: body, actions })); }
    } catch {
      patch(() => ({ role: "assistant", text: FALLBACK, actions: ["whatsapp", "email"], error: true }));
    } finally { setBusy(false); }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen} modal={false}>
      <Dialog.Portal>
        <Dialog.Content data-testid="chat-panel" onInteractOutside={(e) => e.preventDefault()} aria-describedby="chat-desc"
          className="fixed inset-0 z-[45] flex flex-col bg-paper shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[600px] sm:max-h-[calc(100vh-48px)] sm:w-[400px] sm:rounded-3xl sm:border sm:border-line">
          <div className="flex items-center gap-3 border-b border-line px-5 py-4">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-ink text-gold"><Sparkles className="h-4 w-4" /></span>
            <div className="flex-1">
              <Dialog.Title className="font-display text-[16px] font-semibold">Webhaus KI-Assistent</Dialog.Title>
              <p id="chat-desc" className="text-[12px] text-mute">Antwortet nur zu Webhaus · KI kann irren</p>
            </div>
            <Dialog.Close asChild><button type="button" aria-label="Assistent schließen" data-testid="chat-close" className="grid h-11 w-11 place-items-center rounded-full border border-line"><X className="h-4 w-4" /></button></Dialog.Close>
          </div>
          <div ref={listRef} className="thin-scroll flex-1 space-y-4 overflow-y-auto px-5 py-5" aria-live="polite" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={`max-w-[88%] whitespace-pre-line rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${m.role === "user" ? "rounded-br-md bg-ink text-paper" : "rounded-bl-md border border-line bg-white text-ink"}`}>
                  {m.text || (m.streaming ? <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-mute" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-mute [animation-delay:.15s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-mute [animation-delay:.3s]" /></span> : null)}
                </div>
                {m.role === "assistant" && <Actions list={m.actions} onAction={onAction} />}
              </div>
            ))}
            {msgs.length === 1 && (
              <div className="flex flex-col items-start gap-2">
                {SUGGEST.map((s) => <button key={s} type="button" onClick={() => send(s)} data-testid={`chat-suggestion-${SUGGEST.indexOf(s)}`} className="rounded-full border border-gold/50 bg-gold-soft/60 px-4 py-2 text-left text-[13px] font-medium text-ink hover:bg-gold-soft">{s}</button>)}
              </div>
            )}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 border-t border-line p-3">
            <label htmlFor="chat-input" className="sr-only">Ihre Frage</label>
            <input id="chat-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={500} placeholder="Ihre Frage zu Webhaus …" data-testid="chat-input"
              className="h-12 flex-1 rounded-full border border-line bg-white px-4 text-[15px] outline-none focus:border-ink" />
            <button type="submit" disabled={busy || !input.trim()} aria-label="Senden" data-testid="chat-send" className="grid h-12 w-12 place-items-center rounded-full bg-ink text-paper disabled:opacity-40"><Send className="h-4 w-4" /></button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
